import React, { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";

const SUGGESTED = [
  "Mi az a GrowBag?",
  "Hol lesz elérhető?",
  "Hogyan kapok 10% kedvezményt?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Szia! 🌱 Én a GrowBag asszisztens vagyok. Kérdezd meg bátran, mi ez és hogyan működik!" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, loading]);

  const sendMessage = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setMessages((m) => [...m, { role: "user", text: content }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chat", {
        message: content,
        session_id: sessionId,
        provider: "anthropic",
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "Bocsánat, most épp nem tudok válaszolni. Próbáld meg kicsit később." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-widget-toggle"
        aria-label="Chat megnyitása"
        className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-[#2C221E] text-[#FBF8F3] shadow-[0_14px_30px_-8px_rgba(44,34,30,0.5)] flex items-center justify-center hover:bg-[#1a1310] transition-all"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
        {!open && (
          <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-[#C29B78] ring-2 ring-[#FBF8F3]">
            <span className="animate-ping absolute inset-0 rounded-full bg-[#C29B78] opacity-75" />
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-24 right-5 z-40 w-[calc(100vw-2.5rem)] sm:w-[380px] max-h-[70vh] rounded-3xl bg-[#FBF8F3] border border-[#2C221E]/15 shadow-[0_30px_60px_-20px_rgba(44,34,30,0.4)] overflow-hidden flex flex-col"
        >
          <div className="bg-[#2C221E] text-[#FBF8F3] p-4 flex items-center gap-3">
            <span className="h-9 w-9 rounded-xl bg-[#4A6B53] flex items-center justify-center">
              <Sparkles className="h-4 w-4" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-mono-brand text-[10px] tracking-[0.3em] text-[#C29B78]">GROWBAG · ASSZISZTENS</p>
              <p className="text-sm font-semibold">Kérdezd a GrowBag-et</p>
            </div>
            <span className="chip bg-[#4A6B53]/30 text-[#EAF0EC] border-[#4A6B53]/50 text-[9px]">AI</span>
          </div>

          <div ref={bodyRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FBF8F3]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#2C221E] text-[#FBF8F3] rounded-br-sm"
                      : "bg-[#F3EDE2] text-[#2C221E] rounded-bl-sm"
                  }`}
                  data-testid={`chat-msg-${m.role}`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#F3EDE2] text-[#5C524A] rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Gondolkozom...
                </div>
              </div>
            )}
            {messages.length === 1 && !loading && (
              <div className="pt-2 flex flex-wrap gap-2">
                {SUGGESTED.map((q) => (
                  <button
                    key={q}
                    onClick={() => sendMessage(q)}
                    className="text-xs rounded-full border border-[#2C221E]/15 bg-white px-3 py-1.5 text-[#5C524A] hover:border-[#4A6B53] hover:text-[#2C221E] transition-colors"
                    data-testid="chat-suggestion"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage();
            }}
            className="p-3 border-t border-[#2C221E]/10 bg-[#FBF8F3] flex items-center gap-2"
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Írj egy kérdést..."
              maxLength={500}
              disabled={loading}
              data-testid="chat-input"
              className="flex-1 rounded-full border-[#2C221E]/15"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              data-testid="chat-send-button"
              className="h-10 w-10 rounded-full bg-[#2C221E] text-[#FBF8F3] flex items-center justify-center disabled:opacity-40"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
